import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import {
  VehiclePreventiveMaintenanceHistoryItem,
  VehiclePreventiveMaintenanceHistoryPaginatedResponse,
  VehiclePreventiveMaintenanceHistoryService,
  VehiclePreventiveMaintenanceHistoryFilters
} from '../../../../services/vehicle-preventive-maintenance-history.service';
import { TableAction, TableColumn } from '../../../../shared/components/table/table.component';

@Component({
  selector: 'app-preventive-maintenance-history-list',
  templateUrl: './preventive-maintenance-history-list.component.html',
  styleUrls: ['./preventive-maintenance-history-list.component.css']
})
export class PreventiveMaintenanceHistoryListComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Dados
  items: VehiclePreventiveMaintenanceHistoryItem[] = [];
  isLoading = false;
  error: string | null = null;

  // Paginação (backend)
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  hasNextPage = false;
  hasPreviousPage = false;

  // Filtros
  filters: VehiclePreventiveMaintenanceHistoryFilters = {
    vehicleId: null,
    plateNumber: '',
    preventiveMaintenanceId: null
  };
  selectedStatus = '';
  startDate = '';
  endDate = '';
  availableStatuses: string[] = [];

  // Tabela genérica
  tableTitle = 'Histórico de Preventivas';
  showSearch = true;
  searchPlaceholder = 'Buscar por placa (ex: ABC-1234)...';
  clickable = false;

  columns: TableColumn[] = [
    { titulo: 'Veículo', label: 'vehiclePlate' },
    { titulo: 'Preventiva', label: 'preventiveMaintenanceName' },
    {
      titulo: 'Data Execução',
      label: 'executedDate',
      formatter: (value: string | null) => {
        if (!value) return '-';
        const date = new Date(value);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString();
      }
    },
    { titulo: 'Km Execução', label: 'executedMileage' },
    { titulo: 'Status', label: 'status' }
  ];

  actions: TableAction[] = [];

  constructor(
    private historyService: VehiclePreventiveMaintenanceHistoryService
  ) {}

  ngOnInit(): void {
    this.loadHistory();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasClientFilters(): boolean {
    return !!(this.selectedStatus || this.startDate || this.endDate);
  }

  get backendPaginationTotal(): number {
    return this.hasClientFilters ? 0 : this.totalItems;
  }

  get historyTableTitle(): string {
    const count = this.hasClientFilters ? this.items.length : this.totalItems;
    return `Histórico de Preventivas (${count})`;
  }

  loadHistory(page: number = this.currentPage, pageSize: number = this.pageSize): void {
    this.isLoading = true;
    this.error = null;

    const effectivePage = this.hasClientFilters ? 1 : page;
    const effectivePageSize = this.hasClientFilters ? 1000 : pageSize;

    this.historyService.getAllPaginated(effectivePage, effectivePageSize, this.filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: VehiclePreventiveMaintenanceHistoryPaginatedResponse) => {
          let items = response.items;

          // Populate status options from first successful load
          if (this.availableStatuses.length === 0 && items.length > 0) {
            const statuses = [...new Set(items.map(i => i.status).filter(s => !!s && s !== 'Unknown'))];
            this.availableStatuses = statuses.sort();
          }

          // Apply client-side filters
          if (this.selectedStatus) {
            items = items.filter(i => i.status === this.selectedStatus);
          }
          if (this.startDate) {
            const start = new Date(this.startDate);
            start.setHours(0, 0, 0, 0);
            items = items.filter(i => !!i.executedDate && new Date(i.executedDate) >= start);
          }
          if (this.endDate) {
            const end = new Date(this.endDate);
            end.setHours(23, 59, 59, 999);
            items = items.filter(i => !!i.executedDate && new Date(i.executedDate) <= end);
          }

          this.items = items;

          if (this.hasClientFilters) {
            this.hasNextPage = false;
            this.hasPreviousPage = false;
          } else {
            this.currentPage = response.page;
            this.pageSize = response.pageSize;
            this.totalItems = response.totalItems;
            this.totalPages = response.totalPages;
            this.hasNextPage = this.currentPage < this.totalPages;
            this.hasPreviousPage = this.currentPage > 1;
          }

          this.isLoading = false;
        },
        error: (err) => {
          console.error('Erro ao carregar histórico de preventivas:', err);
          this.error = 'Erro ao carregar histórico de preventivas. Tente novamente.';
          this.isLoading = false;
        }
      });
  }

  onTablePageChange(page: number): void {
    this.currentPage = page;
    this.loadHistory(this.currentPage, this.pageSize);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onTablePageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPage = 1;
    this.loadHistory(this.currentPage, this.pageSize);
  }

  retry(): void {
    this.loadHistory(1, this.pageSize);
  }

  applyFilters(): void {
    this.currentPage = 1;
    // Normalizar placa: remover espaços extras
    if (this.filters.plateNumber) {
      this.filters.plateNumber = this.filters.plateNumber.trim();
    }
    this.loadHistory(this.currentPage, this.pageSize);
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadHistory(1, this.pageSize);
  }

  clearFilters(): void {
    this.filters = { vehicleId: null, plateNumber: '', preventiveMaintenanceId: null };
    this.selectedStatus = '';
    this.startDate = '';
    this.endDate = '';
    this.currentPage = 1;
    this.loadHistory(1, this.pageSize);
  }

  onSortChange(event: { sortBy: string; sortOrder: 'asc' | 'desc' }): void {
    this.items = [...this.items].sort((a, b) => {
      const va = String((a as any)[event.sortBy] ?? '');
      const vb = String((b as any)[event.sortBy] ?? '');
      const cmp = va.localeCompare(vb, 'pt-BR', { numeric: true });
      return event.sortOrder === 'asc' ? cmp : -cmp;
    });
  }

  onSearchChange(term: string): void {
    this.filters.plateNumber = term ? term.trim() : '';
    this.applyFilters();
  }
}

